import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, X, RefreshCw, Ticket, Clock, CheckCircle, AlertCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import RequestITForm from "./components/RequestITForm";

const API_BASE = "https://jne-cilacap.com/slip/public/api";

const STATUS_CONFIG = {
  order: {
    label: "Ngantri",
    color: "#f59e0b",
    bg: "#fffbeb",
    icon: <Clock size={12} />,
  },
  sedang: {
    label: "Diproses",
    color: "#3b82f6",
    bg: "#eff6ff",
    icon: <RefreshCw size={12} />,
  },
  received: {
    label: "Selesai",
    color: "#10b981",
    bg: "#ecfdf5",
    icon: <CheckCircle size={12} />,
  },
};

const getStatusCfg = (status) =>
  STATUS_CONFIG[status?.toLowerCase()] || {
    label: status || "-",
    color: "#6b7280",
    bg: "#f3f4f6",
    icon: <AlertCircle size={12} />,
  };

export default function RequestITPage() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const getUserId = () => {
    try {
      return JSON.parse(localStorage.getItem("data_karyawan"))?.id || null;
    } catch {
      return null;
    }
  };

  const fetchTickets = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/request-it?user_created=${userId}`);
      const json = await res.json();
      if (json.status === "success" || Array.isArray(json.data)) {
        setTickets(json.data || []);
      } else if (Array.isArray(json)) {
        setTickets(json);
      }
    } catch (err) {
      console.warn("Gagal fetch IT tickets:", err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    fetchTickets();
  }, [fetchTickets]);

  const handleFormSuccess = () => {
    setShowForm(false);
    fetchTickets();
  };

  /* ─── STYLES ─── */
  const s = {
    page: {
      minHeight: "100vh",
      background: "linear-gradient(180deg, #F5F9FF 0%, #E8F1FF 40%, #FFFFFF 100%)",
      fontFamily: "'Inter', 'Poppins', sans-serif",
      paddingBottom: 110,
    },
    header: {
      padding: "16px 16px 0",
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    backBtn: {
      width: 40,
      height: 40,
      borderRadius: "50%",
      background: "#fff",
      border: "1px solid #e5e7eb",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      flexShrink: 0,
      boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
    },
    headerTitle: {
      flex: 1,
      color: "#111827",
      fontSize: 18,
      fontWeight: 700,
      margin: 0,
    },
    bottomBar: {
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      background: "rgba(255, 255, 255, 0.92)",
      backdropFilter: "blur(16px)",
      WebkitBackdropFilter: "blur(16px)",
      borderTop: "1px solid rgba(0, 0, 0, 0.08)",
      padding: "12px 16px max(14px, env(safe-area-inset-bottom))",
      boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.06)",
      zIndex: 90,
      display: "flex",
      justifyContent: "center",
    },
    bottomActionBtn: {
      width: "100%",
      maxWidth: 480,
      height: 48,
      borderRadius: 14,
      border: "none",
      background: "linear-gradient(135deg, #3b82f6, #6366f1)",
      color: "#fff",
      fontWeight: 700,
      fontSize: 15,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      boxShadow: "0 4px 16px rgba(99, 102, 241, 0.35)",
    },
    heroCard: {
      margin: "18px 16px 0",
      borderRadius: 20,
      padding: "20px 20px 22px",
      background: "linear-gradient(135deg, #3b82f6 0%, #6366f1 50%, #8b5cf6 100%)",
      boxShadow: "0 8px 32px rgba(99,102,241,0.4)",
      position: "relative",
      overflow: "hidden",
    },
    heroTitle: {
      color: "#fff",
      fontSize: 15,
      fontWeight: 600,
      margin: "0 0 4px",
      opacity: 0.9,
    },
    heroSub: {
      color: "rgba(255,255,255,0.7)",
      fontSize: 12.5,
      margin: 0,
    },
    statsRow: {
      display: "flex",
      gap: 10,
      margin: "14px 16px 0",
    },
    statCard: {
      flex: 1,
      borderRadius: 14,
      padding: "14px 12px",
      background: "#fff",
      border: "1px solid #e5e7eb",
      textAlign: "center",
      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    },
    statNum: {
      fontSize: 22,
      fontWeight: 800,
      color: "#111827",
      margin: "0 0 2px",
    },
    statLabel: {
      fontSize: 11,
      color: "#6b7280",
      margin: 0,
    },
    sectionTitle: {
      color: "#6b7280",
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      padding: "20px 16px 8px",
      margin: 0,
    },
    ticketCard: {
      margin: "0 16px 10px",
      borderRadius: 16,
      padding: "16px",
      background: "#fff",
      border: "1px solid #e5e7eb",
      cursor: "pointer",
      transition: "box-shadow 0.2s",
      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    },
    ticketTop: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: 8,
    },
    noTiket: {
      color: "#6366f1",
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: "0.05em",
    },
    ticketDari: {
      color: "#111827",
      fontSize: 14.5,
      fontWeight: 600,
      margin: "2px 0 4px",
    },
    ticketProblem: {
      color: "#6b7280",
      fontSize: 12.5,
      margin: "0 0 10px",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    ticketMeta: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    ticketDate: {
      color: "#9ca3af",
      fontSize: 11.5,
    },
    statusBadge: (cfg) => ({
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "3px 10px",
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 700,
      background: cfg.bg,
      color: cfg.color,
    }),
    emptyWrap: {
      textAlign: "center",
      padding: "60px 20px",
    },
    emptyIcon: {
      fontSize: 48,
      marginBottom: 12,
    },
    emptyTitle: {
      color: "#111827",
      fontSize: 16,
      fontWeight: 600,
      margin: "0 0 6px",
    },
    emptyDesc: {
      color: "#9ca3af",
      fontSize: 13,
      margin: 0,
    },
    /* Modal */
    modalOverlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.6)",
      backdropFilter: "blur(4px)",
      zIndex: 1000,
      display: "flex",
      alignItems: "flex-end",
    },
    modalSheet: {
      width: "100%",
      maxHeight: "92vh",
      overflowY: "auto",
      background: "#fff",
      borderRadius: "24px 24px 0 0",
      padding: "20px 20px 40px",
    },
    modalHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 20,
    },
    modalTitle: {
      fontSize: 17,
      fontWeight: 700,
      color: "#111827",
      margin: 0,
    },
    closeBtn: {
      width: 34,
      height: 34,
      borderRadius: "50%",
      background: "#f3f4f6",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    /* Detail modal */
    detailRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "10px 0",
      borderBottom: "1px solid #f3f4f6",
    },
    detailKey: {
      color: "#6b7280",
      fontSize: 13,
    },
    detailVal: {
      color: "#111827",
      fontSize: 13,
      fontWeight: 600,
      textAlign: "right",
      maxWidth: "60%",
    },
  };

  /* ─── Stats ─── */
  const countByStatus = (st) =>
    tickets.filter((t) => t.status?.toLowerCase() === st).length;

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div style={s.page}>
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />

      {/* ─── HEADER ─── */}
      <div style={s.header}>
        <motion.button
          style={s.backBtn}
          onClick={() => navigate(-1)}
          whileTap={{ scale: 0.9 }}
          aria-label="Kembali"
        >
          <ArrowLeft size={22} color="#111827" />
        </motion.button>

        <h1 style={s.headerTitle}>IT Request</h1>
      </div>

      {/* ─── HERO ─── */}
      <motion.div
        style={s.heroCard}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
      >
        <div
          style={{
            position: "absolute",
            top: -20,
            right: -20,
            width: 100,
            height: 100,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.06)",
          }}
        />
        <Ticket size={28} color="rgba(255,255,255,0.6)" style={{ marginBottom: 8 }} />
        <p style={s.heroTitle}>IT Request System</p>
        <p style={s.heroSub}>
          Laporkan masalah perangkat IT dan pantau status penanganannya
        </p>
      </motion.div>

      {/* ─── STATS ─── */}
      <div style={s.statsRow}>
        {[
          { label: "Total", count: tickets.length, color: "#818cf8" },
          { label: "Selesai", count: countByStatus("received"), color: "#60a5fa" },
          { label: "Proses", count: countByStatus("sedang"), color: "#34d399" },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            style={s.statCard}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + i * 0.05 }}
          >
            <p style={{ ...s.statNum, color: stat.color }}>{stat.count}</p>
            <p style={s.statLabel}>{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* ─── TICKET LIST ─── */}
      <p style={s.sectionTitle}>Riwayat Tiket</p>

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <RefreshCw
            size={28}
            color="#6366f1"
            style={{ animation: "spin 1s linear infinite" }}
          />
        </div>
      ) : tickets.length === 0 ? (
        <div style={s.emptyWrap}>
          <div style={s.emptyIcon}>🎫</div>
          <p style={s.emptyTitle}>Belum ada tiket</p>
          <p style={s.emptyDesc}>
            Tekan "Buat Tiket" untuk melaporkan masalah IT
          </p>
        </div>
      ) : (
        tickets.map((ticket, i) => {
          const cfg = getStatusCfg(ticket.status);
          return (
            <motion.div
              key={ticket.id}
              style={s.ticketCard}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.04 }}
              onClick={() => setSelectedTicket(ticket)}
              whileTap={{ scale: 0.98 }}
              id={`ticket-${ticket.id}`}
            >
              <div style={s.ticketTop}>
                <span style={s.noTiket}>{ticket.no_tiket || `#${ticket.id}`}</span>
                <span style={s.statusBadge(cfg)}>
                  {cfg.icon} {cfg.label}
                </span>
              </div>
              <p style={s.ticketDari}>{ticket.dari || "-"}</p>
              <p style={s.ticketProblem}>{ticket.problem || "-"}</p>
              <div style={s.ticketMeta}>
                <span style={s.ticketDate}>
                  🖥 {ticket.perangkat || "-"}
                </span>
                <span style={s.ticketDate}>
                  {formatDate(ticket.tanggal || ticket.created_at)}
                </span>
              </div>
            </motion.div>
          );
        })
      )}

      {/* ─── BOTTOM ACTION BAR ─── */}
      <div style={s.bottomBar}>
        <motion.button
          style={s.bottomActionBtn}
          onClick={() => setShowForm(true)}
          whileTap={{ scale: 0.97 }}
          id="btn-new-it-request"
        >
          <Plus size={18} strokeWidth={2.5} />
          Buat Tiket IT
        </motion.button>
      </div>

      {/* ─── FORM MODAL (bottom sheet) ─── */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            style={s.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => e.target === e.currentTarget && setShowForm(false)}
          >
            <motion.div
              style={s.modalSheet}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
            >
              <div style={s.modalHeader}>
                <h2 style={s.modalTitle}>🎫 Buat Tiket IT</h2>
                <button
                  style={s.closeBtn}
                  onClick={() => setShowForm(false)}
                  aria-label="Tutup form"
                  id="btn-close-it-form"
                >
                  <X size={18} color="#374151" />
                </button>
              </div>
              <RequestITForm
                onSuccess={handleFormSuccess}
                onClose={() => setShowForm(false)}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── DETAIL MODAL ─── */}
      <AnimatePresence>
        {selectedTicket && (
          <motion.div
            style={s.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) =>
              e.target === e.currentTarget && setSelectedTicket(null)
            }
          >
            <motion.div
              style={s.modalSheet}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
            >
              <div style={s.modalHeader}>
                <h2 style={s.modalTitle}>Detail Tiket</h2>
                <button
                  style={s.closeBtn}
                  onClick={() => setSelectedTicket(null)}
                  aria-label="Tutup detail"
                  id="btn-close-detail"
                >
                  <X size={18} color="#374151" />
                </button>
              </div>

              {/* Status badge */}
              {(() => {
                const cfg = getStatusCfg(selectedTicket.status);
                return (
                  <div style={{ marginBottom: 16 }}>
                    <span
                      style={{
                        ...s.statusBadge(cfg),
                        fontSize: 13,
                        padding: "5px 14px",
                      }}
                    >
                      {cfg.icon} {cfg.label}
                    </span>
                  </div>
                );
              })()}

              {[
                ["No. Tiket", selectedTicket.no_tiket],
                ["Tanggal", formatDate(selectedTicket.tanggal || selectedTicket.created_at)],
                ["Dari / Lokasi", selectedTicket.dari],
                ["Perangkat", selectedTicket.perangkat],
                ["Penanganan", selectedTicket.penanganan],
                ["Nomor / ID", selectedTicket.nomer],
                ["Password", selectedTicket.password],
                ["Pelapor", selectedTicket.pelapor],
                ["WA Pelapor", selectedTicket.wa_pelapor],
                ["Oleh (IT)", selectedTicket.oleh],
              ]
                .filter(([, v]) => v)
                .map(([key, val]) => (
                  <div style={s.detailRow} key={key}>
                    <span style={s.detailKey}>{key}</span>
                    <span style={s.detailVal}>{val}</span>
                  </div>
                ))}

              {selectedTicket.problem && (
                <div style={{ marginTop: 16 }}>
                  <p style={{ ...s.detailKey, marginBottom: 6 }}>Keterangan Masalah</p>
                  <p
                    style={{
                      background: "#f8fafc",
                      borderRadius: 12,
                      padding: "12px 14px",
                      fontSize: 13,
                      color: "#374151",
                      margin: 0,
                      lineHeight: 1.6,
                    }}
                  >
                    {selectedTicket.problem}
                  </p>
                </div>
              )}

              {selectedTicket.catatan && (
                <div style={{ marginTop: 14 }}>
                  <p style={{ ...s.detailKey, marginBottom: 6 }}>Catatan IT</p>
                  <p
                    style={{
                      background: "#fffbeb",
                      borderRadius: 12,
                      padding: "12px 14px",
                      fontSize: 13,
                      color: "#92400e",
                      margin: 0,
                      lineHeight: 1.6,
                    }}
                  >
                    {selectedTicket.catatan}
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @keyframes spin { from { transform:rotate(0deg) } to { transform:rotate(360deg) } }
      `}</style>
    </div>
  );
}
